import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useBackgroundImageService } from 'hooks/use-background-image-service.hook';

import { NextButton } from 'components/next-button';
import { Typography } from 'components/typography';
import { ModalWindow } from 'components/modal-window';

import { TypographyVariants } from 'constants/typography-variants';

import { useStyles } from './styles';

interface SkipTrialModalWindowProps {
    showSkipTrialModalWindow: boolean;
    upgradePlanMonthsCount: number;
    handleSkipClick: () => void;
    handleUpgradeClick: () => void;
}

export const SkipTrialModalWindow: FC<SkipTrialModalWindowProps> = (
    { showSkipTrialModalWindow, upgradePlanMonthsCount, handleSkipClick, handleUpgradeClick }
    ): JSX.Element => {
    const { t } = useTranslation();

    const { modalWrapper, modal, title, boldText, subtitle, listItemWrapper, listItemIcon, listItemDescription,
        infoWrapper, infoText, buttonsWrapper, skipButtonWrapper, skipButton, startButtonWrapper, startButton } = useStyles();

    const pageImages = useBackgroundImageService([
        'svg/upgrade-plan/skip-trial-modal-window/weights.svg', 'svg/upgrade-plan/skip-trial-modal-window/unlimited.svg',
        'svg/upgrade-plan/skip-trial-modal-window/cutlery.svg', 'svg/upgrade-plan/skip-trial-modal-window/graph.svg'
    ]);

    return (
        <>
        {showSkipTrialModalWindow &&
            <ModalWindow wrapperClassName={modalWrapper} modalWindowFormClassName={modal} hasCloseIcon={false}>
                <div>
                    <Typography variant={TypographyVariants.h2} className={title}>
                        <Trans i18nKey='upgrade-plan-skip-trial-modal-window-title' components={[<em className={boldText}></em>]}/>
                    </Typography>

                    <Typography variant={TypographyVariants.h2} className={subtitle}>
                        <Trans i18nKey='upgrade-plan-skip-trial-modal-window-subtitle' />
                    </Typography>

                    <div>
                        <div className={listItemWrapper}>
                            <img className={listItemIcon} src={pageImages[0]} alt='Weights' />
                            <Typography className={listItemDescription} variant={TypographyVariants.h3}>
                                <Trans i18nKey='upgrade-plan-modal-what-you-will-get-option-1' />
                            </Typography>
                        </div>

                        <div className={listItemWrapper}>
                            <img className={listItemIcon} src={pageImages[1]} alt='Coach' />
                            <Typography className={listItemDescription} variant={TypographyVariants.h3}>
                                <Trans i18nKey='upgrade-plan-modal-what-you-will-get-option-2' />
                            </Typography>
                        </div>

                        <div className={listItemWrapper}>
                            <img className={listItemIcon} src={pageImages[2]} alt='Cutlery' />
                            <Typography className={listItemDescription} variant={TypographyVariants.h3}>
                                <Trans i18nKey='upgrade-plan-modal-what-you-will-get-option-3' />
                            </Typography>
                        </div>

                        <div className={listItemWrapper}>
                            <img className={listItemIcon} src={pageImages[3]} alt='Graph' />
                            <Typography className={listItemDescription} variant={TypographyVariants.h3}>
                                <Trans i18nKey='upgrade-plan-modal-what-you-will-get-option-4' />
                            </Typography>
                        </div>
                    </div>
                </div>

                <div className={infoWrapper}>
                    <Typography className={infoText} variant={TypographyVariants.h3}>
                        <Trans i18nKey='upgrade-plan-skip-trial-modal-window-info-text' values={{
                            monthsCountToReachGoalWeight: upgradePlanMonthsCount,
                            count: upgradePlanMonthsCount
                        }} />
                    </Typography>
                </div>

                <div className={buttonsWrapper}>
                    <NextButton
                        typographyText={t('upgrade-plan-skip-trial-modal-window-button-skip')}
                        buttonWrapperClassName={skipButtonWrapper}
                        className={skipButton}
                        onClick={handleSkipClick} />

                    <NextButton
                        typographyText={t('upgrade-plan-skip-trial-modal-window-button-start', { upgradePlanMonthsCount })}
                        buttonWrapperClassName={startButtonWrapper}
                        className={startButton}
                        onClick={handleUpgradeClick} />
                </div>
            </ModalWindow>}
        </>
    );
};
